import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppStore } from '../store/useAppStore';

interface PersistentTabConfig<T extends string> {
    id: T;
}

interface UsePersistentSubtabOptions<T extends string> {
    defaultTab: T;
    deferInvalidRequestedSubtab?: boolean;
    omitQueryParamForDefault?: boolean;
    persistDefaultOnMount?: boolean;
    queryParam?: string;
    routePath?: string | readonly string[];
    surfaceId: string;
    tabs: readonly PersistentTabConfig<T>[];
    updateSearchParams?: (searchParams: URLSearchParams, tabId: T) => void;
}

type SearchParamSetter = ReturnType<typeof useSearchParams>[1];
type SearchParamMutation = (searchParams: URLSearchParams) => void;

let pendingSearchParamBatch: {
    mutations: SearchParamMutation[];
    setSearchParams: SearchParamSetter;
} | null = null;

function enqueueSearchParamUpdate(
    setSearchParams: SearchParamSetter,
    mutateSearchParams: SearchParamMutation,
) {
    if (pendingSearchParamBatch) {
        pendingSearchParamBatch.mutations.push(mutateSearchParams);
        pendingSearchParamBatch.setSearchParams = setSearchParams;
        return;
    }

    pendingSearchParamBatch = {
        mutations: [mutateSearchParams],
        setSearchParams,
    };

    const flushSearchParamBatch = () => {
        const batch = pendingSearchParamBatch;
        pendingSearchParamBatch = null;

        if (!batch) {
            return;
        }

        batch.setSearchParams((currentSearchParams) => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            for (const mutation of batch.mutations) {
                mutation(nextSearchParams);
            }
            return nextSearchParams;
        }, { replace: true });
    };

    if (typeof queueMicrotask === 'function') {
        queueMicrotask(flushSearchParamBatch);
    } else {
        void Promise.resolve().then(flushSearchParamBatch);
    }
}

export function usePersistentSubtab<T extends string>({
    defaultTab,
    deferInvalidRequestedSubtab = false,
    omitQueryParamForDefault = false,
    persistDefaultOnMount = true,
    queryParam = 'tab',
    routePath,
    surfaceId,
    tabs,
    updateSearchParams,
}: UsePersistentSubtabOptions<T>): {
    activeSubtab: T;
    handleSubtabChange: (tabId: string) => void;
} {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const pendingUserSubtabRef = useRef<T | null>(null);
    const persistedSubtab = useAppStore(state => state.ui.surfaceSubtabs[surfaceId]);
    const setSurfaceSubtab = useAppStore(state => state.metaActions.setSurfaceSubtab);
    const tabIds = useMemo(() => tabs.map(tab => tab.id), [tabs]);
    const tabIdSet = useMemo(() => new Set<string>(tabIds), [tabIds]);
    const fallbackTab = tabIdSet.has(defaultTab)
        ? defaultTab
        : tabIds[0] ?? defaultTab;
    const canSyncUrl = useMemo(() => {
        if (!routePath) {
            return true;
        }

        const paths = Array.isArray(routePath) ? routePath : [routePath];
        return paths.includes(location.pathname);
    }, [location.pathname, routePath]);
    const requestedSubtab = canSyncUrl ? searchParams.get(queryParam) : null;
    const hasRequestedSubtab = canSyncUrl && searchParams.has(queryParam);

    const resolveSubtab = useCallback((tabId: string | null | undefined): T | null => tabId && tabIdSet.has(tabId) ? tabId as T : null, [tabIdSet]);
    const resolvedRequestedSubtab = resolveSubtab(requestedSubtab);
    const resolvedPersistedSubtab = resolveSubtab(persistedSubtab);
    const shouldDeferSubtabResolution = deferInvalidRequestedSubtab
        && (
            (hasRequestedSubtab && !resolvedRequestedSubtab)
            || (!hasRequestedSubtab && Boolean(persistedSubtab) && !resolvedPersistedSubtab)
        );

    const activeSubtab = useMemo(() => {
        if (hasRequestedSubtab) {
            return resolvedRequestedSubtab ?? fallbackTab;
        }

        return resolvedPersistedSubtab ?? fallbackTab;
    }, [fallbackTab, hasRequestedSubtab, resolvedPersistedSubtab, resolvedRequestedSubtab]);

    const writeSubtabParam = useCallback((tabId: T) => {
        if (!canSyncUrl) {
            return;
        }

        enqueueSearchParamUpdate(setSearchParams, (nextSearchParams) => {
            if (omitQueryParamForDefault && tabId === fallbackTab) {
                nextSearchParams.delete(queryParam);
            } else {
                nextSearchParams.set(queryParam, tabId);
            }
            updateSearchParams?.(nextSearchParams, tabId);
        });
    }, [canSyncUrl, fallbackTab, omitQueryParamForDefault, queryParam, setSearchParams, updateSearchParams]);

    const handleSubtabChange = useCallback((tabId: string) => {
        const nextSubtab = resolveSubtab(tabId) ?? fallbackTab;
        pendingUserSubtabRef.current = nextSubtab;
        if (resolvedPersistedSubtab !== nextSubtab) {
            setSurfaceSubtab(surfaceId, nextSubtab);
        }
        writeSubtabParam(nextSubtab);
    }, [fallbackTab, resolveSubtab, resolvedPersistedSubtab, setSurfaceSubtab, surfaceId, writeSubtabParam]);

    useEffect(() => {
        if (shouldDeferSubtabResolution) {
            return;
        }

        const pendingUserSubtab = pendingUserSubtabRef.current;
        const resolvedPendingUserSubtab = resolveSubtab(pendingUserSubtab);

        if (pendingUserSubtab && !resolvedPendingUserSubtab) {
            pendingUserSubtabRef.current = null;
        }

        if (
            resolvedPendingUserSubtab
            && resolvedPersistedSubtab === resolvedPendingUserSubtab
            && activeSubtab !== resolvedPendingUserSubtab
        ) {
            return;
        }

        if (resolvedPendingUserSubtab && activeSubtab === resolvedPendingUserSubtab) {
            pendingUserSubtabRef.current = null;
        }

        const isImplicitDefault = !hasRequestedSubtab && !persistedSubtab && activeSubtab === fallbackTab;
        if (isImplicitDefault && !persistDefaultOnMount) {
            return;
        }

        if (resolvedPersistedSubtab !== activeSubtab) {
            setSurfaceSubtab(surfaceId, activeSubtab);
        }

        const shouldOmitActiveQueryParam = omitQueryParamForDefault && activeSubtab === fallbackTab;
        const shouldWriteQueryParam = shouldOmitActiveQueryParam
            ? searchParams.has(queryParam)
            : searchParams.get(queryParam) !== activeSubtab;

        if (shouldWriteQueryParam) {
            writeSubtabParam(activeSubtab);
        }
    }, [activeSubtab, fallbackTab, hasRequestedSubtab, omitQueryParamForDefault, persistedSubtab, persistDefaultOnMount, queryParam, resolveSubtab, resolvedPersistedSubtab, searchParams, setSurfaceSubtab, shouldDeferSubtabResolution, surfaceId, writeSubtabParam]);

    return { activeSubtab, handleSubtabChange };
}

export function usePersistentScrollRestoration(surfaceId: string): {
    handleScroll: React.UIEventHandler<HTMLElement>;
    scrollRef: React.RefCallback<HTMLElement>;
} {
    const savedScrollTop = useAppStore(state => state.ui.surfaceScrollPositions[surfaceId] ?? 0);
    const setSurfaceScrollPosition = useAppStore(state => state.metaActions.setSurfaceScrollPosition);
    const nodeRef = useRef<HTMLElement | null>(null);
    const latestScrollTopRef = useRef(savedScrollTop);

    const flushScrollPosition = useCallback(() => {
        const scrollTop = nodeRef.current?.scrollTop ?? latestScrollTopRef.current;
        setSurfaceScrollPosition(surfaceId, scrollTop);
    }, [setSurfaceScrollPosition, surfaceId]);

    const scrollRef = useCallback<React.RefCallback<HTMLElement>>((node) => {
        nodeRef.current = node;

        if (node) {
            node.scrollTop = savedScrollTop;
            latestScrollTopRef.current = savedScrollTop;
        }
    }, [savedScrollTop]);

    const handleScroll = useCallback<React.UIEventHandler<HTMLElement>>((event) => {
        latestScrollTopRef.current = event.currentTarget.scrollTop;
    }, []);

    useLayoutEffect(() => {
        const node = nodeRef.current;

        if (node) {
            node.scrollTop = savedScrollTop;
            latestScrollTopRef.current = savedScrollTop;
        }

        return () => {
            flushScrollPosition();
        };
    }, [flushScrollPosition, savedScrollTop]);

    useEffect(() => {
        window.addEventListener('pagehide', flushScrollPosition);

        return () => {
            window.removeEventListener('pagehide', flushScrollPosition);
        };
    }, [flushScrollPosition]);

    return { handleScroll, scrollRef };
}
